import type { ReactNode } from "react"
import { Link } from "@inertiajs/react"
import ProductLink from "@/components/productQuickView/ProductLink"
import { Card } from "@/components/ui/Card"

export interface ListRow {
  label: string
  value: ReactNode
  /** Makes the label a link that opens this product's quick view. */
  productExternalId?: string
}

interface Props {
  title: string
  rows: ListRow[]
  footer?: { label: string; href: string }
  hint?: string
  /** Shown in place of the rows when there are none. */
  empty?: string
}

/** Titled card of "label … value" rows, with an optional link to the full page. */
export default function ListCard({ title, rows, footer, hint, empty }: Props) {
  return (
    <Card className="flex flex-col px-5 py-4">
      <div className="mb-2 flex items-start justify-between gap-4">
        <div className="text-sm font-semibold">{title}</div>
        {footer && (
          <Link href={footer.href} className="shrink-0 text-sm font-medium text-link underline-offset-4 hover:underline">
            {footer.label} →
          </Link>
        )}
      </div>
      {rows.length === 0 && empty && <div className="py-2 text-sm">{empty}</div>}
      <div className="flex flex-col divide-y">
        {rows.map((row) => (
          <div key={row.productExternalId ?? row.label} className="flex items-center justify-between gap-4 py-2 text-sm">
            {row.productExternalId ? (
              <ProductLink externalId={row.productExternalId} title={row.label}>
                <span className="truncate">{row.label}</span>
              </ProductLink>
            ) : (
              <span className="truncate">{row.label}</span>
            )}
            <span className="shrink-0 tabular-nums">{row.value}</span>
          </div>
        ))}
      </div>
      {hint && <div className="mt-2 text-xs text-muted-foreground">{hint}</div>}
    </Card>
  )
}
